import {
  Box,
  Button,
  Container,
  Paper,
  Stack,
  // Link,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import {
  ConditionalRenderer,
  ConfirmationDeleteModal,
  Header,
  useLanguageTranslator,
} from "../../../../components";
import { LeftArrowIcon } from "../../../../@lib/icons";
import { RightSide } from "./rightSide";
import { LeftSide } from "./leftSide";
import { useLocation, useNavigate } from "react-router-dom";
import { gql, useMutation } from "@apollo/client";
import { useApolloQuery } from "../../../../hooks";
import { useNotify } from "../../../../components/@common/NotificationsProvider";
import { useCallback, useState } from "react";
import { Policy } from "../../../../@lib/types";
import { useAuthSession } from "../../../../components/AuthSessionProvider";
import {
  statusCode,
  status as statusKey,
  toastTypesKeys,
} from "../../../../utils/constants";
import { ToastContainer } from "react-toastify";
import { toastHandler } from "../../../../utils/helpers";
import { ConfirmationDialogs } from "../features";

const policyQueryDocument = gql`
  query policyQuery($policyId: ID!) {
    policy(id: $policyId) {
      id
      title
      description
      documentTitle
      country {
        name
      }
      submittedBy {
        firstName
        lastName
      }
      policyCategory {
        name
      }
      practices {
        title
      }
      people {
        title
        firstname
        lastname
      }
      theme {
        title
      }
      geographicalArea {
        name
      }
      picture {
        source
        originalFilename
      }
      rejectionReason
      document {
        source
        originalFilename
      }
      link
      date
      status
      statusLabel
    }
  }
`;

const practiceQueryDocument = gql`
  query practiceQuery($practiceId: ID!) {
    practice(id: $practiceId) {
      id
      title
      description
      documentTitle
      country {
        name
      }
      submittedBy {
        firstName
        lastName
      }
      practiceCategory {
        name
      }
      theme {
        title
      }
      practiceType {
        name
      }
      policy {
        title
      }
      policies {
        title
      }
        rejectionReason
      people {
        title
        firstname
        lastname
      }
      picture {
        source
        originalFilename
      }
      document {
        source
        originalFilename
      }
      link
      date
      status
      statusLabel
    }
  }
`;

const policyDeleteMutationDocument = gql`
  mutation policyDeleteMutation($policy: PolicyDeleteInput!) {
    policyDelete(input: $policy) {
      policy {
        id
      }
    }
  }
`;

const deletePracticeMutationDocument = gql`
  mutation DeletePractice($practiceInput: PracticeDeleteInput!) {
    practiceDelete(input: $practiceInput) {
      practice {
        id
      }
    }
  }
`;

const policyStatusUpdate = gql`
  mutation UpdatePolicyStatus($policyInput: PolicyStatusUpdateInput!) {
    policyStatusUpdate(input: $policyInput) {
      clientMutationId
      policy {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        geographicalArea {
          name
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policyCategory {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
    }
  }
`;

const practiceStatusUpdate = gql`
  mutation UpdatePracticeStatus($practiceInput: PracticeStatusUpdateInput!) {
    practiceStatusUpdate(input: $practiceInput) {
      clientMutationId
      practice {
        country {
          name
        }
        createdAt
        date
        description
        document {
          source
          originalFilename
        }
        id
        link
        picture {
          source
          originalFilename
        }
        policy {
          title
        }
        practiceCategory {
          name
        }
        practiceType {
          name
        }
        status
        theme {
          title
        }
        title
        updatedAt
      }
    }
  }
`;

const moduleIDKeys = {
  policy: 1,
  practice: 2,
};

function isModule(location: any) {
  const isPractices = location?.pathname?.includes("practices") || false;
  const isPolicies = location?.pathname?.includes("policies") || false;

  const path = location?.pathname?.split("/");

  if (isPolicies) {
    return {
      queryDocument: policyQueryDocument,
      createPath: "/admin/policies/new",
      heading: "Policies",
      params: { policyId: path?.[3] },
      deleteDocument: policyDeleteMutationDocument,
      deletePath: `/admin/policies`,
      mainPath: `/admin/policies`,
      moduleName: "policy",
      moduleID: moduleIDKeys.policy,
      statusUpdateGQL: policyStatusUpdate,
    };
  } else if (isPractices) {
    return {
      queryDocument: practiceQueryDocument,
      createPath: "/admin/practices/new",
      deletePath: `/admin/practices`,
      mainPath: `/admin/practices`,
      heading: "Practices",
      moduleName: "practice",
      params: { practiceId: path?.[3] },
      deleteDocument: deletePracticeMutationDocument,
      moduleID: moduleIDKeys.practice,
      statusUpdateGQL: practiceStatusUpdate,
    };
  } else {
    return {
      queryDocument: gql``,
      createPath: "",
      deletePath: "",
      mainPath: "",
      moduleName: "",
      heading: "",
      params: {},
      deleteDocument: gql``,
    };
  }
}

function AdminDetailsView() {
  const [isOpen, setIsOpen] = useState<any>({
    status: false,
    currId: null,
    moduleCode: 2,
  });
  const [rejectReason, setRejectReason] = useState("");
  const [isError, setIsError] = useState(false);

  const { user } = useAuthSession() as any;
  const location = useLocation();
  const navigate = useNavigate();
  const { translator } = useLanguageTranslator();
  const path = location?.pathname?.split("/");
  const notify = useNotify();

  const {
    queryDocument,
    moduleID,
    heading,
    params,
    deleteDocument,
    statusUpdateGQL,
    deletePath,
    mainPath,
    moduleName,
  }: any = isModule(location);

  const { data, loading, refetch } = useApolloQuery({
    queryDocument: queryDocument,
    variables: params,
  });

  const [deleteMutation, { loading: deletingPolicy }] = useMutation(
    deleteDocument,
    {
      context: {
        headers: {
          "access-token": user?.accessToken,
          client: user?.client,
          uid: user?.email,
        },
      },
    }
  );

  const [statusUpdate] = useMutation(statusUpdateGQL, {
    context: {
      headers: {
        "access-token": user?.accessToken,
        client: user?.client,
        uid: user?.email,
      },
    },
  });

  const handleDeletePolicy = useCallback(
    async (module: Pick<Policy, "id" | "title">, moduleID: number) => {
      if (moduleID === moduleIDKeys?.policy) {
        try {
          await deleteMutation({ variables: { policy: { id: module.id } } });
          notify.success(`Policy "${module.title}" deleted successfuly!`);
          navigate(deletePath);
        } catch (err) {
          notify.error(err as Error);
        }
        return;
      }
      try {
        await deleteMutation({
          variables: {
            practiceInput: {
              id: module.id,
            },
          },
        });
        notify.success(`Practice "${module.title}" deleted successfuly!`);
        navigate(deletePath);
      } catch (err) {
        notify.error(err as Error);
      }
    },
    [deleteMutation, refetch, notify]
  );

  async function onClickHandler(id: number) {
    if (isOpen?.moduleCode === statusCode.approved) {
      try {
        const response = await statusUpdate({
          variables:
            moduleID === moduleIDKeys?.policy
              ? { policyInput: { id: Number(id), status: "Approved" } }
              : { practiceInput: { id: Number(id), status: "Approved" } },
        });
        if (response?.data) {
          setIsOpen({ status: false, currId: null, module: "" });
          toastHandler("Status is Updated", toastTypesKeys.success);
          setTimeout(() => {
            refetch();
          }, 2300);
        }
      } catch (error) {
        console.error("Error:", error);
        setIsOpen({ status: false, currId: null, module: "" });
        setIsError(true);
      }
      return;
    }

    if (!rejectReason)
      return toastHandler("Please enter the Reason", toastTypesKeys.error);

    try {
      const response = await statusUpdate({
        variables:
          moduleID === moduleIDKeys?.policy
            ? {
                policyInput: {
                  id: Number(id),
                  status: "Rejected",
                  reason: rejectReason,
                },
              }
            : {
                practiceInput: {
                  id: Number(id),
                  status: "Rejected",
                  reason: rejectReason,
                },
              },
      });
      if (response?.data) {
        setIsOpen({ status: false, currId: null, module: "" });
        toastHandler("Status is Updated", toastTypesKeys.success);
        setTimeout(() => {
          refetch();
        }, 2300);
        setRejectReason("");
      }
    } catch (error) {
      console.error("Error:", error);
      setIsOpen({ status: false, currId: null, module: "" });
      setRejectReason("");
      setIsError(true);
    }
  }

  function onAdminActions(id: any, type: any) {
    setIsOpen({ status: true, currId: id, moduleCode: type });
  }

  const responseStatus = data?.practice?.status || data?.policy?.status;
  const responseId = data?.practice?.id || data?.policy?.id;

  const [isOpenDelete, setIsOpenDelete] = useState({ status: false, data: {} });

  function onClickButtonDeleteHandler(clickedItem: any) {
    setIsOpenDelete({ status: true, data: clickedItem });
  }

  async function onClickDeleteHandler(data: any) {
    await handleDeletePolicy(data, moduleID);
  }

  function handleDeleteClose() {
    setIsOpenDelete({ status: false, data: {} });
  }

  return (
    <>
      <ConfirmationDeleteModal
        isOpen={isOpenDelete?.status}
        item={isOpenDelete?.data}
        onClickHandler={onClickDeleteHandler}
        handleClose={handleDeleteClose}
      />
      <ToastContainer />
      <Header
        title={translator("Admin Area")}
        subtitle=""
      />
      <ConfirmationDialogs
        text={moduleName}
        viewPath={`${mainPath}/${isOpen?.currId}`}
        isOpen={isOpen}
        onClickHandler={onClickHandler}
        setIsOpen={setIsOpen}
        rejectReason={rejectReason}
        setRejectReason={setRejectReason}
        isError={isError}
        setIsError={setIsError}
      />
      <Container sx={{ my: 3 }}>
        <Paper component={Container} sx={{ py: 3 }}>
          <Stack direction={{ lg: "row" }} gap={2}>
            <Box>
              <Link
                style={{
                  alignItems: "center",
                  display: "flex",
                  fontSize: "14px",
                  color: "#979da7",
                  textDecoration: "none",
                  fontWeight: "700",
                }}
                to={mainPath}
              >
                <LeftArrowIcon /> {translator("Go back")}
              </Link>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                width: "80%",
              }}
            >
              <Typography variant="h4">
                {translator("Viewing")} {translator(heading)}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      </Container>

      <Container sx={{ my: 3 }}>
        <Stack direction={{ lg: "row" }}>
          <Paper
            component={Container}
            sx={{ py: 3, flex: 3, borderRadius: "0px", boxShadow: "none" }}
          >
            <LeftSide data={data?.policy || data?.practice} />
          </Paper>
          <Paper
            component={Container}
            sx={{ py: 3, flex: 1, borderRadius: "0px", boxShadow: "none" }}
          >
            <RightSide data={data?.policy || data?.practice} />
          </Paper>
        </Stack>
        <Stack
          direction={{ lg: "row" }}
          justifyContent={{ lg: "end" }}
          gap={1}
          sx={{ backgroundColor: "white", pr: 2, pb: 1 }}
        >
          <Button
            sx={{
              px: 6,
              backgroundColor:
                responseStatus === statusKey?.approved ||
                responseStatus === statusKey?.approved
                  ? "#FF6D6D"
                  : "#52D777",
              color: "#ffffff",
              "&:hover": {
                backgroundColor:
                  responseStatus === statusKey?.approved
                    ? "#CC4C4C"
                    : "#3BAA50",
              },
              fontSize: "14px",
            }}
            onClick={() =>
              onAdminActions &&
              onAdminActions(
                responseId,
                responseStatus === statusKey?.approved
                  ? statusCode.rejected
                  : statusCode.approved
              )
            }
          >
            {responseStatus === statusKey?.approved
              ? translator("status.Reject")
              : translator("status.Approve")}
          </Button>
          <ConditionalRenderer
            condition={responseStatus === statusKey?.pending}
          >
            <Button
              sx={{
                px: 6,
                backgroundColor: "#ff6d6d",
                color: "#ffffff",
                "&:hover": {
                  backgroundColor: "#D9534F",
                },
                fontSize: "14px",
              }}
              onClick={() =>
                onAdminActions &&
                onAdminActions(responseId, statusCode.rejected)
              }
            >
              {translator("status.Reject")}
            </Button>
          </ConditionalRenderer>
          {/* <Link
            sx={{
              px: 6,
              backgroundColor: "#F6A000",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#D48B00",
              },
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              fontSize: "14px",
            }}
            href={`/${path?.[2]}/${path?.[3]}/edit`}
          >
            {translator("generic.EDIT")}
          </Link> */}
          <Link
            style={{
              padding: 6,
              backgroundColor: "#F6A000",
              color: "#ffffff",
              width: "13%",
              // "&:hover": {
              //   backgroundColor: "#D48B00",
              // },
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textDecoration: "none",
              fontSize: "14px",
            }}
            to={`/${path?.[1]}/${path?.[2]}/${path?.[3]}/edit`}
          >
            {translator("generic.EDIT")}
          </Link>
          <Button
            sx={{
              px: 6,
              backgroundColor: "#CD0A0A",
              color: "#ffffff",
              "&:hover": {
                backgroundColor: "#A10808",
              },
              fontSize: "14px",
            }}
            onClick={() =>
              onClickButtonDeleteHandler(data?.policy || data?.practice)
            }
          >
            {translator("generic.DELETE")}
          </Button>
        </Stack>
      </Container>
    </>
  );
}

export { AdminDetailsView };
